/**
 * Dietary Filters Module
 * 
 * Provides pattern-based filtering for ingredients and inspirations
 * based on user dietary restrictions. Patterns are multilingual to
 * support country-specific ingredient names.
 * 
 * @module dietaryFilters
 */

// Normalization map: UI dietary keys → filter pattern keys
// The UI uses keys like "noDairy" but filter patterns may use different keys
const DIETARY_KEY_NORMALIZATION = {
  noDairy: 'noDairy',
  dairyFree: 'noDairy',     // alias
  lactoseFree: 'lactoseFree',
  noNuts: 'noNuts',
  noMushrooms: 'noMushrooms',
  noShellfish: 'noShellfish',
  noEggs: 'noEggs',
  noSoy: 'noSoy',
  noFish: 'noFish',
  noPork: 'noPork',
  halal: 'halal',
  glutenFree: 'glutenFree',
  vegetarian: 'vegetarian',
  vegan: 'vegan'
};

const GENERIC_MILK_PATTERN = /milk/i;
const PLANT_MILK_PATTERN = /\b(oat|soy|soya|almond|rice|coconut|plant|pea|hemp)\s+milk\b/i;
const NON_DAIRY_PLANT_DRINK_PATTERN = /\b(?:milk-free|dairy-free|non-dairy|non dairy|plant-based|plant based|plant)\b[\w\s-]{0,40}\b(?:oat|soy|soya|almond|rice|coconut|pea|hemp)\b[\w\s-]{0,20}\b(?:drink|beverage|milk)\b|\b(?:oat|soy|soya|almond|rice|coconut|pea|hemp)\b[\w\s-]{0,20}\b(?:drink|beverage)\b/i;
const EXPLICIT_DAIRY_MILK_PATTERN = /\b(whole|skim|semi-skimmed|buttermilk|cow'?s?|goat|sheep|dairy|condensed|evaporated)\s+milk\b|\bmilk\s+chocolate\b/i;
const CREAM_PATTERN = /\bcream\b/i;
const BUTTER_PATTERN = /\bbutter\b/i;
const NON_DAIRY_BUTTER_PATTERN = /\b(?:almond|peanut|cashew|hazelnut|sunflower|pumpkin|sesame|seed|nut|cocoa|cacao|tahini|vegan|plant[-\s]?based|dairy[-\s]?free|non[-\s]?dairy)\b[\w\s-]{0,30}\bbutter\b|\bbutter\s+(?:beans?|lettuce)\b/i;
const SPANISH_BREAD_PAN_PATTERN = /\bpan\b(?=\s+(?:de\s+(?:molde|trigo|centeno|cebada|espelta|pita|hamburguesa|hot\s*dog)|rallado|integral|blanco|tostado|baguette|chapata|pay[eé]s|gallego|multicereal))/i;
const PAN_COOKING_METHOD_PATTERN = /\b(?:one[-\s]?pan|sheet[-\s]?pan|pan[-\s](?:seared|fried|roasted|cooked|charred|grilled|sauteed|sautéed|toasted|roast|sauce|style)|pan\s+(?:roast|sauce|style|juices|drippings))\b/i;
const GLUTEN_FREE_CONTEXT_TERMS = new Set([
  'bread', 'toast', 'baguette', 'sourdough', 'flatbread', 'wrap', 'pan', 'pain', 'brot',
  'pasta', 'spaghetti', 'penne', 'flour', 'oats', 'oat', 'avena', 'couscous', 'cuscus', 'pancake', 'waffle', 'crepe', 'crêpe'
]);
const GLUTEN_FREE_QUALIFIER_BEFORE = '(?:certified\\s+)?(?:gluten[-\\s]?free|gf)';
const GLUTEN_FREE_QUALIFIER_AFTER = '(?:gluten[-\\s]?free|gf|sin\\s+gluten|sans\\s+gluten|senza\\s+glutine)';
const NEGATED_RESTRICTION_SUFFIX_PATTERN = /^[-\s]*(?:free|less)\b/i;
const NEGATED_RESTRICTION_PREFIX_PATTERN = /\b(?:no|without)\s+$/i;

const isAllowedPlantMilkTerm = (value) =>
  (PLANT_MILK_PATTERN.test(value) || NON_DAIRY_PLANT_DRINK_PATTERN.test(value)) &&
  !EXPLICIT_DAIRY_MILK_PATTERN.test(value);

const isAllowedNonDairyButterTerm = (value) => NON_DAIRY_BUTTER_PATTERN.test(value);

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const toGlobalPattern = (pattern) => new RegExp(
  pattern.source,
  pattern.flags.includes('g') ? pattern.flags : `${pattern.flags}g`
);

const normalizeMatchedTerm = (match) => match
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim();

const isNegatedRestrictionMatch = (value, match) => {
  const matchedText = match?.[0] || '';
  const start = typeof match?.index === 'number' ? match.index : value.indexOf(matchedText);
  if (start < 0 || !matchedText) return false;

  const end = start + matchedText.length;
  const after = value.slice(end, end + 12);
  if (NEGATED_RESTRICTION_SUFFIX_PATTERN.test(after)) return true;

  const before = value.slice(Math.max(0, start - 16), start);
  return NEGATED_RESTRICTION_PREFIX_PATTERN.test(before);
};

const isAllowedGlutenFreeTerm = (value, match) => {
  const term = normalizeMatchedTerm(match);

  if (term === 'pan' && PAN_COOKING_METHOD_PATTERN.test(value)) {
    return true;
  }

  if (!GLUTEN_FREE_CONTEXT_TERMS.has(term)) {
    return false;
  }

  const escapedTerm = escapeRegExp(term);
  const qualifierBeforeTerm = new RegExp(`\\b${GLUTEN_FREE_QUALIFIER_BEFORE}(?:\\s+[a-z-]+){0,3}\\s+${escapedTerm}\\b`, 'i');
  const qualifierAfterTerm = new RegExp(`\\b${escapedTerm}\\b(?:\\s+[a-z-]+){0,3}\\s+${GLUTEN_FREE_QUALIFIER_AFTER}\\b`, 'i');

  return qualifierBeforeTerm.test(value) || qualifierAfterTerm.test(value);
};

/**
 * Ingredient filter patterns for dietary restrictions.
 * Used to filter out conflicting ingredients from budget tier hints and inspirations.
 * 
 * Each restriction has:
 * - excludeCategories: Array of category names to exclude entirely (e.g., ['dairy'])
 * - patterns: Array of RegExp to match against ingredient strings (multilingual)
 */
const RESTRICTION_FILTER_PATTERNS = {
  // Dairy restrictions (UI key: noDairy)
  noDairy: {
    excludeCategories: ['dairy'],
    patterns: [
      // Milk
      GENERIC_MILK_PATTERN, /leche/i, /lait/i, /milch/i,
      // Cheese (generic + specific varieties)
      /cheese/i, /queso/i, /fromage/i, /käse/i,
      /manchego/i, /parmesano/i, /parmesan/i, /parmigiano/i,
      /mozzarella/i, /cheddar/i, /gruyère/i, /gruyere/i,
      /burrata/i, /stilton/i, /gorgonzola/i,
      /mascarpone/i, /ricotta/i, /requesón/i,
      /queso rallado/i, /queso en lonchas/i, /queso fresco/i,
      /queso crema/i, /queso de cabra/i,
      // Dish names that imply dairy even if the cheese/butter word is omitted
      /caprese/i, /kyiv/i, /kiev/i,
      // Butter
      BUTTER_PATTERN, /mantequilla/i, /beurre/i,
      // Yogurt
      /yogurt/i, /yoghurt/i, /yogur/i,
      // Cream
      CREAM_PATTERN, /crema/i, /nata/i, /crème/i, /sahne/i, /sour cream/i,
      /nata para cocinar/i, /nata para montar/i,
      // Dairy-based sauces/dressings
      /\branch\b/i, /\bbéchamel\b/i, /\bbechamel\b/i, /\balfredo\b/i,
      /hollandaise/i, /b[eé]arnaise/i, /bearnaise/i
    ]
  },
  
  lactoseFree: {
    excludeCategories: ['dairy'],
    patterns: [
      /milk/i, /leche/i, /lait/i, /milch/i,
      CREAM_PATTERN, /crema/i, /nata/i, /crème/i, /sahne/i,
      /yogurt/i, /yoghurt/i, /yogur/i,
      /ice cream/i, /helado/i
    ]
  },
  
  // Nut restrictions
  noNuts: {
    excludeCategories: [],
    patterns: [
      // Generic nut terms
      /\bnut\b/i, /\bnuts\b/i, /nuez/i, /nueces/i, /noix/i, /nuss/i,
      // Specific nuts (EN + ES + FR + DE)
      /almond/i, /almendra/i, /amande/i, /mandel/i,
      /pine nut/i, /piñon/i, /piñones/i, /pignon/i, /pinienkern/i,
      /walnut/i, /nuez/i, /noix/i,
      /pecan/i, /pecana/i,
      /cashew/i, /anacardo/i,
      /pistachio/i, /pistacho/i,
      /hazelnut/i, /avellana/i, /noisette/i, /haselnuss/i,
      /peanut/i, /cacahuete/i, /cacahuate/i,
      // Almond products
      /almendra laminada/i, /almendra molida/i
    ]
  },
  
  // Egg restrictions
  noEggs: {
    excludeCategories: [],
    patterns: [
      /\begg\b/i, /\beggs\b/i,
      /huevo/i, /huevos/i,
      /oeuf/i, /oeufs/i,
      /\bei\b/i, /\beier\b/i,
      /omelette/i, /tortilla española/i, /tortilla francesa/i, /frittata/i, /quiche/i,
      /mayonnaise/i, /mayonesa/i, /\bmayo\b/i, /aioli/i, /custard/i, /meringue/i,
      /hollandaise/i, /b[eé]arnaise/i, /bearnaise/i,
      /french toast/i, /pancake/i, /waffle/i, /cr[eê]pe/i,
      // Common formats that normally rely on egg binder/wash
      /fish cakes?/i, /kyiv/i, /kiev/i
    ]
  },
  
  // Soy restrictions
  noSoy: {
    excludeCategories: [],
    patterns: [
      /\bsoy\b/i, /\bsoja\b/i,
      /\btofu\b/i, /tempeh/i, /edamame/i, /miso/i,
      /soy sauce/i, /salsa de soja/i, /sauce soja/i, /sojasauce/i
    ]
  },
  
  // Fish restrictions (excludes fish but not shellfish)
  noFish: {
    excludeCategories: [],
    patterns: [
      // Generic fish terms
      /\bfish\b/i, /pescado/i, /poisson/i, /\bfisch\b/i,
      // Salmon
      /salmon/i, /salmón/i, /saumon/i, /lachs/i,
      // Tuna
      /tuna/i, /atún/i, /thon/i, /thunfisch/i,
      // Cod
      /\bcod\b/i, /bacalao/i, /cabillaud/i, /kabeljau/i,
      // Sea bass
      /sea bass/i, /lubina/i, /\bbar\b/i,
      // sea bass
      /merluza/i, /sea bass/i,
      // Sardines
      /sardine/i, /sardina/i, /sardinas/i,
      // Anchovies
      /anchovy/i, /anchoa/i, /anchoas/i, /anchois/i,
      // Trout
      /trout/i, /trucha/i, /truite/i, /forelle/i,
      // Mackerel
      /mackerel/i, /caballa/i, /maquereau/i, /makrele/i,
      // Bream / Dorada
      /dorada/i, /bream/i, /daurade/i,
      // Monkfish / Rape
      /monkfish/i, /\brape\b/i, /lotte/i,
      // Octopus / Pulpo (technically cephalopod but often grouped with fish)
      /octopus/i, /pulpo/i, /poulpe/i,
      // Canned fish products
      /atún en lata/i, /sardinas en lata/i, /caballa en lata/i,
      /anchoas en aceite/i
    ]
  },
  
  // Pork restrictions
  noPork: {
    excludeCategories: [],
    patterns: [
      // Generic pork terms
      /\bpork\b/i, /cerdo/i, /\bporc\b/i, /schwein/i,
      // Ham
      /\bham\b/i, /jamón/i, /jamón ibérico/i, /jamón cocido/i,
      /jambon/i, /schinken/i,
      // Bacon / Panceta
      /bacon/i, /panceta/i, /beicon/i,
      // Lard
      /lard/i, /manteca/i,
      // Sausages
      /chorizo/i, /sausage/i, /salchicha/i, /salchichas/i,
      /salami/i, /prosciutto/i,
      // Spanish pork cuts
      /lomo de cerdo/i, /solomillo de cerdo/i,
      /lomo/i, /secreto/i, /presa/i, /cochinillo/i,
      /solomillo ibérico/i, /presa ibérica/i, /secreto ibérico/i,
      // Processed pork
      /carne picada de cerdo/i, /fiambre/i
    ]
  },

  halal: {
    excludeCategories: [],
    patterns: [
      // Pork and pork-derived products
      /\bpork\b/i, /cerdo/i, /\bporc\b/i, /schwein/i,
      /\bham\b/i, /jamón/i, /jamon/i, /jambon/i, /schinken/i,
      /bacon/i, /panceta/i, /beicon/i,
      /chorizo/i, /prosciutto/i, /pancetta/i, /salami/i, /pepperoni/i,
      /lard/i, /manteca/i, /pork fat/i, /gelatin/i, /gelatine/i, /g[eé]latine/i,
      /pork sausage/i, /sausage/i, /salchicha/i, /salchichas/i,
      /lomo de cerdo/i, /solomillo de cerdo/i,
      /secreto/i, /presa/i, /cochinillo/i,
      /carne picada de cerdo/i,
      // Alcoholic cooking ingredients
      /\balcohol\b/i, /wine/i, /vino/i, /vin\b/i,
      /sherry/i, /jerez/i, /beer/i, /cerveza/i, /bi[eè]re/i, /birra/i,
      /brandy/i, /cognac/i, /rum/i, /\bron\b/i,
      /vermouth/i, /marsala/i, /port wine/i, /liqueur/i,
      /sake/i, /mirin/i, /shaoxing/i, /rice wine/i
    ]
  },
  
  // Mushroom restrictions
  noMushrooms: {
    excludeCategories: [],
    patterns: [
      /mushroom/i, /seta/i, /champiñon/i, /champignon/i, /pilz/i,
      /fungi/i,
      /porcini/i, /boletus/i, /níscalo/i,
      /shiitake/i, /portobello/i,
      /pfifferling/i, /steinpilz/i,
      /truffle/i, /trufa/i, /tartufo/i, /trüffel/i
    ]
  },
  
  // Shellfish restrictions
  noShellfish: {
    excludeCategories: [],
    patterns: [
      // Shrimp / Prawns
      /shrimp/i, /prawn/i, /gamba/i, /gambas/i, /gambones/i,
      /crevette/i, /garnele/i,
      // Lobster
      /lobster/i, /langosta/i, /bogavante/i, /homard/i, /hummer/i,
      // Crab
      /crab/i, /cangrejo/i, /crabe/i, /krabbe/i,
      // Mussels
      /mussel/i, /mejillon/i, /mejillón/i, /moule/i,
      // Clams
      /clam/i, /almeja/i, /palourde/i,
      // Oysters
      /oyster/i, /ostra/i, /huître/i,
      // Scallops
      /scallop/i, /vieira/i, /vieiras/i, /coquille/i,
      // Cephalopods commonly grouped with shellfish/seafood restrictions
      /squid/i, /calamari/i, /calamar/i, /calamares/i,
      /octopus/i, /pulpo/i, /poulpe/i,
      /crayfish/i, /langoustine/i, /langoustines/i, /langostino/i, /langostinos/i,
      /seafood mix/i
    ]
  },
  
  // Gluten restrictions
  glutenFree: {
    excludeCategories: [],
    patterns: [
      // Bread
      /\bbread\b/i, /\btoast\b/i, /\bbaguette\b/i, /\bsourdough\b/i, /\bflatbread\b/i, /\bwrap\b/i,
      SPANISH_BREAD_PAN_PATTERN, /\bpain\b/i, /\bbrot\b/i,
      // Pasta
      /\bpasta\b/i, /\bspaghetti\b/i, /\bpenne\b/i,
      // Flour
      /\bflour\b/i, /harina/i, /harina de trigo/i, /farine/i, /mehl/i,
      // Wheat
      /\bwheat\b/i, /trigo/i, /blé/i, /weizen/i,
      // Couscous
      /\bcouscous\b/i, /cuscús/i,
      // Barley / rye equivalents
      /barley/i, /rye/i, /cebada/i, /orge/i, /gerste/i,
      // Breadcrumbs
      /breadcrumb/i, /chapelure/i,
      // Common wheat-based breakfast formats
      /pancake/i, /waffle/i, /french toast/i, /cr[eê]pe/i,
      // Oats (unless certified GF)
      /\boats\b/i, /avena/i
    ]
  },
  
  // Vegetarian - exclude meat and fish
  vegetarian: {
    excludeCategories: [],
    patterns: [
      // Poultry (EN + ES + FR + DE)
      /chicken/i, /pollo/i, /poulet/i, /hähnchen/i,
      /pechugas de pollo/i, /muslos de pollo/i, /contramuslos/i, /jamoncitos/i,
      /turkey/i, /pavo/i, /dinde/i, /pute/i,
      /duck/i, /pato/i, /canard/i, /ente/i,
      // Red meat - Beef
      /beef/i, /ternera/i, /boeuf/i, /rind/i,
      /chuletón/i, /entrecot/i, /solomillo/i, /filetes de ternera/i,
      // Red meat - Pork
      /\bpork\b/i, /cerdo/i, /\bporc\b/i, /schwein/i,
      /lomo de cerdo/i, /solomillo de cerdo/i,
      /secreto/i, /presa/i, /cochinillo/i,
      // Red meat - Lamb
      /lamb/i, /cordero/i, /agneau/i, /lamm/i,
      // Fish
      /\bfish\b/i, /pescado/i, /poisson/i, /\bfisch\b/i,
      /salmon/i, /salmón/i, /saumon/i, /lachs/i,
      /tuna/i, /atún/i, /thon/i, /thunfisch/i,
      /\bcod\b/i, /bacalao/i, /cabillaud/i, /kabeljau/i,
      /sea bass/i, /lubina/i, /\bbar\b/i,
      /merluza/i, /dorada/i, /\brape\b/i, /pulpo/i,
      // Shellfish
      /prawn/i, /gamba/i, /gambas/i, /gambones/i, /shrimp/i,
      /lobster/i, /langosta/i, /bogavante/i, /homard/i, /hummer/i,
      /crab/i, /cangrejo/i, /crabe/i, /krabbe/i,
      /mussel/i, /mejillon/i, /mejillón/i, /moule/i,
      /clam/i, /almeja/i, /palourde/i,
      /oyster/i, /ostra/i, /huître/i,
      /scallop/i, /vieira/i, /vieiras/i, /coquille/i,
      /squid/i, /calamari/i, /calamar/i, /calamares/i,
      /octopus/i, /pulpo/i, /poulpe/i,
      /crayfish/i, /langoustine/i, /langoustines/i, /langostino/i, /langostinos/i,
      /seafood mix/i,
      // Processed meat
      /bacon/i, /sausage/i, /salchicha/i, /salchichas/i, /chorizo/i,
      /\bham\b/i, /jamón/i, /fiambre/i,
      /mince/i, /carne/i, /carne picada/i, /\bmeat\b/i, /viande/i, /fleisch/i,
      // Canned meat/fish
      /atún en lata/i, /sardinas en lata/i, /caballa en lata/i,
      /pollo en conserva/i, /anchoas/i
    ]
  },
  
  // Vegan - exclude all animal products
  vegan: {
    excludeCategories: ['dairy'],
    patterns: [
      // Poultry (EN + ES + FR + DE)
      /chicken/i, /pollo/i, /poulet/i, /hähnchen/i,
      /pechugas de pollo/i, /muslos de pollo/i, /contramuslos/i, /jamoncitos/i,
      /turkey/i, /pavo/i, /dinde/i, /pute/i,
      /duck/i, /pato/i, /canard/i, /ente/i,
      // Red meat - Beef
      /beef/i, /ternera/i, /boeuf/i, /rind/i,
      /chuletón/i, /entrecot/i, /solomillo/i, /filetes de ternera/i,
      // Red meat - Pork
      /\bpork\b/i, /cerdo/i, /\bporc\b/i, /schwein/i,
      /lomo de cerdo/i, /solomillo de cerdo/i,
      /secreto/i, /presa/i, /cochinillo/i,
      // Red meat - Lamb
      /lamb/i, /cordero/i, /agneau/i, /lamm/i,
      // Fish
      /\bfish\b/i, /pescado/i, /poisson/i, /\bfisch\b/i,
      /salmon/i, /salmón/i, /saumon/i, /lachs/i,
      /tuna/i, /atún/i, /thon/i, /thunfisch/i,
      /\bcod\b/i, /bacalao/i, /cabillaud/i,
      /merluza/i, /dorada/i, /\brape\b/i, /pulpo/i,
      // Shellfish
      /prawn/i, /gamba/i, /gambas/i, /gambones/i, /shrimp/i,
      /lobster/i, /langosta/i, /bogavante/i, /homard/i, /hummer/i,
      /crab/i, /cangrejo/i, /crabe/i, /krabbe/i,
      /mussel/i, /mejillon/i, /mejillón/i, /moule/i,
      /clam/i, /almeja/i, /palourde/i,
      /oyster/i, /ostra/i, /huître/i,
      /scallop/i, /vieira/i, /vieiras/i, /coquille/i,
      /squid/i, /calamari/i, /calamar/i, /calamares/i,
      /octopus/i, /pulpo/i, /poulpe/i,
      /crayfish/i, /langoustine/i, /langoustines/i, /langostino/i, /langostinos/i,
      /seafood mix/i,
      // Eggs
      /\begg\b/i, /\beggs\b/i, /huevo/i, /huevos/i, /oeuf/i, /\bei\b/i,
      /huevos camperos/i,
      // Processed meat
      /bacon/i, /sausage/i, /salchicha/i, /chorizo/i,
      /\bham\b/i, /jamón/i, /fiambre/i,
      // Other animal products
      /honey/i, /miel/i,
      /\bmeat\b/i, /carne/i, /viande/i, /fleisch/i,
      // Canned meat/fish
      /atún en lata/i, /sardinas en lata/i, /caballa en lata/i,
      /pollo en conserva/i, /anchoas/i
    ]
  }
};

/**
 * Normalizes dietary restriction keys from UI format to filter pattern keys.
 * 
 * @param {string[]} keys - Raw dietary restriction keys from UI
 * @returns {string[]} Normalized keys that match RESTRICTION_FILTER_PATTERNS
 */
export const normalizeDietaryKeys = (keys) => {
  if (!keys?.length) return [];
  return keys.map(key => DIETARY_KEY_NORMALIZATION[key] || key);
};

/**
 * Filters an ingredient/inspiration list based on dietary restrictions.
 * Normalizes restriction keys before looking them up in RESTRICTION_FILTER_PATTERNS.
 * 
 * @param {string[]} items - Array of ingredient or inspiration strings to filter
 * @param {string[]} restrictionKeys - Array of dietary restriction keys (e.g., ["noDairy", "noNuts"])
 * @returns {string[]} Filtered list with restricted items removed
 */
export const filterByRestrictions = (items, restrictionKeys) => {
  if (!items?.length || !restrictionKeys?.length) return items;

  return items.filter(item => getRestrictionMatches(item, restrictionKeys).length === 0);
};

export const getRestrictionMatches = (item, restrictionKeys) => {
  if (item === null || item === undefined || !restrictionKeys?.length) return [];

  const value = String(item);
  const lowerItem = value.toLowerCase();
  const normalizedKeys = normalizeDietaryKeys(restrictionKeys);
  const matches = [];

  for (const restrictionKey of normalizedKeys) {
    const filter = RESTRICTION_FILTER_PATTERNS[restrictionKey];
    if (!filter) continue;

    let matchedForRestriction = false;

    for (const pattern of filter.patterns) {
      if (restrictionKey === 'noDairy' && pattern === GENERIC_MILK_PATTERN && isAllowedPlantMilkTerm(lowerItem)) {
        continue;
      }
      if (restrictionKey === 'noDairy' && pattern === BUTTER_PATTERN && isAllowedNonDairyButterTerm(lowerItem)) {
        continue;
      }

      for (const match of lowerItem.matchAll(toGlobalPattern(pattern))) {
        if (isNegatedRestrictionMatch(lowerItem, match)) {
          continue;
        }
        if (restrictionKey === 'glutenFree' && isAllowedGlutenFreeTerm(lowerItem, match[0])) {
          continue;
        }

        matches.push({
          restrictionKey,
          match: match[0].trim(),
          value,
        });
        matchedForRestriction = true;
        break;
      }

      if (matchedForRestriction) break;
    }
  }

  return matches;
};

/**
 * Checks if a specific category should be excluded based on dietary restrictions.
 * 
 * @param {string} category - Category name to check (e.g., 'dairy')
 * @param {string[]} restrictionKeys - Array of dietary restriction keys
 * @returns {boolean} True if the category should be excluded
 */
export const shouldExcludeCategory = (category, restrictionKeys) => {
  if (!restrictionKeys?.length) return false;
  const normalizedKeys = normalizeDietaryKeys(restrictionKeys);
  return normalizedKeys.some(key => 
    RESTRICTION_FILTER_PATTERNS[key]?.excludeCategories?.includes(category)
  );
};

/**
 * Convenience function to check if dairy should be excluded.
 * 
 * @param {string[]} restrictionKeys - Array of dietary restriction keys
 * @returns {boolean} True if dairy should be excluded
 */
export const shouldExcludeDairy = (restrictionKeys) => {
  return shouldExcludeCategory('dairy', restrictionKeys);
};

/**
 * Gets the list of all supported restriction keys.
 * Useful for validation.
 * 
 * @returns {string[]} Array of supported restriction keys
 */
export const getSupportedRestrictionKeys = () => {
  return Object.keys(RESTRICTION_FILTER_PATTERNS);
};

/**
 * Checks if a restriction key is supported.
 * 
 * @param {string} key - Restriction key to check
 * @returns {boolean} True if the key is supported (after normalization)
 */
export const isRestrictionSupported = (key) => {
  const normalizedKey = DIETARY_KEY_NORMALIZATION[key] || key;
  return RESTRICTION_FILTER_PATTERNS.hasOwnProperty(normalizedKey);
};

/**
 * Human-readable forbidden ingredient lists for LLM prompts.
 * These are explicit English ingredient names that LLM can understand.
 * Used to generate FORBIDDEN INGREDIENTS section in meal plan prompts.
 */
const FORBIDDEN_INGREDIENTS_LIST = {
  noDairy: [
    'dairy milk', 'cow milk', 'whole milk', 'skim milk', 'semi-skimmed milk', 'buttermilk',
    'cheese', 'cheddar', 'mozzarella', 'parmesan', 'feta', 'gouda', 'brie', 'cream cheese', 'cottage cheese', 'ricotta', 'mascarpone', 'goat cheese', 'blue cheese', 'gruyere',
    'butter', 'salted butter', 'unsalted butter',
    'cream', 'heavy cream', 'whipping cream', 'sour cream', 'double cream', 'single cream', 'clotted cream',
    'yogurt', 'greek yogurt', 'plain yogurt', 'natural yogurt',
    'ice cream', 'custard', 'crème fraîche',
    'ranch', 'ranch dressing', 'béchamel', 'alfredo sauce', 'hollandaise sauce', 'béarnaise sauce'
  ],
  lactoseFree: [
    'milk', 'whole milk', 'skim milk',
    'cream', 'heavy cream', 'whipping cream', 'sour cream',
    'yogurt', 'greek yogurt',
    'ice cream', 'custard'
  ],
  noNuts: [
    'almonds', 'almond flour', 'almond milk', 'almond butter',
    'walnuts', 'pecans', 'cashews', 'pistachios', 'hazelnuts', 'macadamia nuts',
    'peanuts', 'peanut butter', 'peanut oil',
    'pine nuts', 'brazil nuts', 'chestnuts',
    'mixed nuts', 'nut butter', 'nut milk'
  ],
  noEggs: [
    'eggs', 'egg', 'egg whites', 'egg yolks', 'whole eggs',
    'mayonnaise', 'mayo', 'aioli',
    'meringue', 'custard', 'quiche', 'frittata', 'omelette', 'hollandaise sauce', 'béarnaise sauce'
  ],
  noSoy: [
    'soy sauce', 'soya sauce', 'tofu', 'tempeh', 'edamame', 'miso', 'miso paste',
    'soy milk', 'soya milk', 'soy protein', 'textured vegetable protein', 'TVP'
  ],
  noFish: [
    'fish', 'salmon', 'tuna', 'cod', 'haddock', 'mackerel', 'sardines', 'anchovies',
    'sea bass', 'trout', 'halibut', 'tilapia', 'sole', 'monkfish', 'bream', 'sea bass',
    'fish sauce', 'fish stock', 'canned tuna', 'canned salmon', 'smoked salmon'
  ],
  noPork: [
    'pork', 'bacon', 'ham', 'prosciutto', 'pancetta', 'chorizo', 'sausage', 'pork sausage',
    'pork chops', 'pork loin', 'pork belly', 'pork tenderloin', 'pork ribs',
    'ground pork', 'minced pork', 'lard', 'pork fat', 'salami', 'pepperoni'
  ],
  halal: [
    'pork', 'bacon', 'ham', 'prosciutto', 'pancetta', 'chorizo', 'salami', 'pepperoni',
    'pork sausage', 'pork chops', 'pork loin', 'pork belly', 'pork tenderloin', 'pork ribs',
    'ground pork', 'minced pork', 'lard', 'pork fat', 'gelatin from pork',
    'wine', 'red wine', 'white wine', 'rice wine', 'shaoxing wine', 'sherry', 'beer',
    'brandy', 'cognac', 'rum', 'marsala', 'vermouth', 'port wine', 'liqueur', 'sake', 'mirin'
  ],
  noMushrooms: [
    'mushrooms', 'button mushrooms', 'cremini mushrooms', 'portobello mushrooms',
    'shiitake mushrooms', 'oyster mushrooms', 'porcini mushrooms', 'chanterelles',
    'truffle', 'truffle oil', 'mushroom stock'
  ],
  noShellfish: [
    'shrimp', 'prawns', 'lobster', 'crab', 'crayfish', 'langoustines',
    'mussels', 'clams', 'oysters', 'scallops', 'squid', 'calamari', 'octopus',
    'shellfish stock', 'seafood mix'
  ],
  vegetarian: [
    'beef', 'chicken', 'pork', 'lamb', 'veal', 'duck', 'turkey', 'goose',
    'fish', 'salmon', 'tuna', 'cod', 'shrimp', 'prawns', 'lobster', 'crab',
    'mussels', 'clams', 'oysters', 'scallops', 'squid', 'calamari', 'octopus', 'seafood mix',
    'bacon', 'ham', 'sausage', 'chorizo', 'salami', 'prosciutto',
    'ground beef', 'minced beef', 'ground pork', 'chicken breast', 'chicken thighs',
    'beef stock', 'chicken stock', 'fish stock', 'bone broth',
    'gelatin', 'lard', 'anchovy paste', 'fish sauce', 'oyster sauce'
  ],
  vegan: [
    'beef', 'chicken', 'pork', 'lamb', 'veal', 'duck', 'turkey', 'goose',
    'fish', 'salmon', 'tuna', 'cod', 'shrimp', 'prawns', 'lobster', 'crab',
    'mussels', 'clams', 'oysters', 'scallops', 'squid', 'calamari', 'octopus', 'seafood mix',
    'bacon', 'ham', 'sausage', 'chorizo', 'salami', 'prosciutto',
    'eggs', 'egg', 'mayonnaise',
    'milk', 'cheese', 'butter', 'cream', 'yogurt', 'ice cream',
    'honey', 'gelatin', 'lard',
    'beef stock', 'chicken stock', 'fish stock', 'bone broth',
    'whey', 'casein', 'ghee'
  ]
};

/**
 * Gets a list of forbidden ingredients for given restriction keys.
 * Used in LLM prompts to explicitly list what should not be used.
 * 
 * @param {string[]} restrictionKeys - Array of dietary restriction keys
 * @returns {string[]} Array of forbidden ingredient names (deduplicated)
 */
export const getForbiddenIngredients = (restrictionKeys) => {
  if (!restrictionKeys || restrictionKeys.length === 0) return [];
  
  const forbiddenSet = new Set();
  
  for (const key of restrictionKeys) {
    const normalizedKey = DIETARY_KEY_NORMALIZATION[key] || key;
    const ingredients = FORBIDDEN_INGREDIENTS_LIST[normalizedKey];
    
    if (ingredients) {
      ingredients.forEach(item => forbiddenSet.add(item));
    }
  }
  
  return Array.from(forbiddenSet);
};

/**
 * Gets a formatted string of forbidden ingredients for LLM prompts.
 * 
 * @param {string[]} restrictionKeys - Array of dietary restriction keys
 * @returns {string} Comma-separated list of forbidden ingredients
 */
export const getForbiddenIngredientsText = (restrictionKeys) => {
  const forbidden = getForbiddenIngredients(restrictionKeys);
  if (forbidden.length === 0) return '';
  
  return forbidden.join(', ');
};

// Export patterns for advanced use cases (e.g., testing)
export { RESTRICTION_FILTER_PATTERNS, DIETARY_KEY_NORMALIZATION };
